import React, { useState, useEffect } from 'react';
import { Pencil, Save, X, Trash2, Plus, Check, User } from 'lucide-react';
import { Table, Container, Form, Button, ButtonGroup, Row, Col } from 'react-bootstrap';
import { useAuth } from '../Components/AuthContext';
import { useParams, Link } from 'react-router-dom';

const UserTable = () => {
  const [users, setUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    active: true,
    userRoles: []
  });

  const AVAILABLE_ROLES = [
    'UMS_READ',
    'UMS_WRITE',
    'ADMIN',
    'UMS_DELETE'
  ];
  
  useEffect(() => {
    fetchUsers();
  }, []);

  const { tenantDomain } = useParams();
  const { user, updateTenantDomain } = useAuth();
  useEffect(() => {
      updateTenantDomain(tenantDomain);
  }, [tenantDomain]);


  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/list-users`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const hasDeleteRoles = () => {
    try {
      let userObj = JSON.parse(user);
      return userObj && userObj.roles && 
        userObj.roles.includes('ADMIN') && 
        userObj.roles.includes('UMS_DELETE');
    } catch (error) {
      return false;
    }
  };
  
  const hasWritePermission = () => {
    try {
      let userObj = JSON.parse(user);
      return userObj && userObj.roles && 
        (userObj.roles.includes('UMS_WRITE') || userObj.roles.includes('ADMIN'));
    } catch (error) {
      return false;
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/user/${userId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });
        
        if (response.ok) {
          await fetchUsers();
        }
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleRoleChange = (e) => {
    const role = e.target.name;
    const isChecked = e.target.checked;
    
    setEditForm(prevForm => ({
      ...prevForm,
      userRoles: isChecked 
        ? [...(prevForm.userRoles || []), role]
        : (prevForm.userRoles || []).filter(r => r !== role)
    }));
  };

  const handleEdit = (user) => {
    setEditingId(user.userId);
    setEditForm({
        ...user,
        userRoles: [...user.userRoles] // Make a copy of the roles array
      });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };
  
  const handleAddUserClick = () => {
    setIsAddingUser(true);
  };
  
  const handleCancelAdd = () => {
    setIsAddingUser(false);
    setNewUser({
      email: '',
      firstName: '',
      lastName: '',
      active: true,
      userRoles: []
    });
  };
  
  const handleNewUserChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setNewUser({
      ...newUser,
      [e.target.name]: value
    });
  };
  
  const handleNewUserRoleChange = (e) => {
    const role = e.target.name;
    const isChecked = e.target.checked;
    
    setNewUser(prevForm => ({
      ...prevForm,
      userRoles: isChecked 
        ? [...(prevForm.userRoles || []), role]
        : (prevForm.userRoles || []).filter(r => r !== role)
    }));
  };
  
  const handleCreateUser = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newUser),
      });
      
      if (response.ok) {
        await fetchUsers();
        handleCancelAdd();
      } else {
        console.error('Error creating user:', await response.text());
        alert('Failed to create user. Please check the form and try again.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('An error occurred while creating the user.');
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setEditForm({
      ...editForm,
      [e.target.name]: value
    });
  };

  const handleSave = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/user/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });
      if (response.ok) {
        await fetchUsers();
        setEditingId(null);
        setEditForm({});
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  return (
    <Container fluid className="mt-4">
      {hasWritePermission() && (
        <div className="mb-3 text-end">
          <Button 
            variant="primary" 
            onClick={handleAddUserClick}
            disabled={isAddingUser}
            title="Add new user"
          >
            <Plus className="h-4 w-4 me-1" /> Add User
          </Button>
        </div>
      )}
      <Table hover bordered responsive>
        <thead>
          <tr>
            <th>Email</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Active</th>
            <th>Roles</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isAddingUser && (
            <tr>
              <td>
                <Form.Control
                  type="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleNewUserChange}
                  placeholder="Email"
                  required
                />
              </td>
              <td>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={newUser.firstName}
                  onChange={handleNewUserChange}
                  placeholder="First Name"
                  required
                />
              </td>
              <td>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={newUser.lastName}
                  onChange={handleNewUserChange}
                  placeholder="Last Name"
                  required
                />
              </td>
              <td>
                <Form.Check
                  type="checkbox"
                  name="active"
                  label="Active"
                  checked={newUser.active}
                  onChange={handleNewUserChange}
                />
              </td>
              <td>
                <div className="d-flex flex-column gap-1">
                  {AVAILABLE_ROLES.map(role => (
                    <Form.Check
                      key={role}
                      type="checkbox"
                      id={`newuser-role-${role}`}
                      name={role}
                      label={role}
                      checked={newUser.userRoles?.includes(role)}
                      onChange={handleNewUserRoleChange}
                    />
                  ))}
                </div>
              </td>
              <td>
                <ButtonGroup>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleCreateUser}
                    title="Create"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleCancelAdd}
                    title="Cancel"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </ButtonGroup>
              </td>
            </tr>
          )}
          {users.map(user => (
            <tr key={user.userId}>
              <td>
                {editingId === user.userId ? (
                  <Form.Control
                    type="email"
                    name="email"
                    value={editForm.email || ''}
                    onChange={handleChange}
                  />
                ) : (
                  user.email
                )}
              </td>
              <td>
                {editingId === user.userId ? (
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={editForm.firstName || ''}
                    onChange={handleChange}
                  />
                ) : (
                  user.firstName
                )}
              </td>
              <td>
                {editingId === user.userId ? (
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={editForm.lastName || ''}
                    onChange={handleChange}
                  />
                ) : (
                  user.lastName
                )}
              </td>
              <td>
                {editingId === user.userId ? (
                  <Form.Check
                      type="checkbox"
                      name="active"
                      label="Active"
                      checked={editForm.active}
                      onChange={handleChange}
                    />
                ) : (
                    <Form.Check 
                    type="checkbox"
                    label="Active"
                    checked={user.active}
                    onChange={handleChange}
                />
                )}
              </td>
              <td>
                  {editingId === user.userId ? (
                    <div className="d-flex flex-column gap-1">
                      {AVAILABLE_ROLES.map(role => (
                        <Form.Check
                          key={role}
                          type="checkbox"
                          id={`role-${user.userId}-${role}`}
                          name={role}
                          label={role}
                          checked={editForm.userRoles?.includes(role)}
                          onChange={handleRoleChange}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="d-flex flex-column">
                      {user.userRoles?.map(role => (
                        <div key={role}>{role}</div>
                      ))}
                    </div>
                  )}
                </td>
              <td>
                {editingId === user.userId ? (
                  <ButtonGroup>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleSave(user.userId)}
                      title="Save"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleCancel}
                      title="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </ButtonGroup>
                ) : (
                  <ButtonGroup>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleEdit(user)}
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {hasDeleteRoles() && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(user.userId)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </ButtonGroup>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default UserTable;
